import { useMutation, useQuery } from "@apollo/client";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import {
  Alert,
  Box,
  Button,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { GenericCommentStream } from "components/Comments/GenericCommentStream";
import { LinearIcon } from "components/LinearIcon";
import { SubmissionRevisionBanner } from "components/Submission/SubmissionRevisionBanner";
import { SubmissionStatusChipWithPopover } from "components/Submission/SubmissionStatusChipWithPopover";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import { gql } from "generated-graphql";
import { CommentThreadFragment, SubmissionEncampStatus } from "generated-graphql/graphql";
import { useBreadcrumb } from "hooks/useBreadcrumbs";
import { useCurrentUser } from "hooks/useCurrentUser";
import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { formatDateLikeForDisplay } from "util/dates";

import { BoldSignDetailsDialog } from "./BoldSignDetailsDialog";
import { WASTE_FULFILLMENT_SEARCH_KEY } from "./GridSection";
import { WasteSubmissionLinearIssueDialog } from "./WasteSubmissionLinearIssueDialog";

const WASTE_SUBMISSION_DETAIL_QUERY = gql(`
  query WasteSubmissionDetailForStaffLanding($submissionId: ID!) {
    wasteSubmissionDetail(submissionId: $submissionId) {
      tenantName
      facilityId
      facilityName
      state
      epaIds
      ownerName
      ownerEmail
      staffReviewer {
        id
        email
        firstName
        lastName
        isSigner
      }
      submission {
        id
        tenantId
        title
        dueDate
        status
        encampStatus
        submissionType
        usesStepper
        revisedFromSubmissionId
        revisedFromSubmission {
          id
          title
        }
        revisionSubmissions {
          id
          title
        }
        processedManifestExceptionReport {
          id
          manifestTrackingNumber
        }
      }
      internalComments {
        id
        reportId
        userId
        workflowId
        activityId
        submissionId
        comment
        isSystemMessage
        createdAt
        user {
          id
          email
          person {
            first
            last
          }
        }
      }
    }
  }
`);

const ADD_SUBMISSION_INTERNAL_COMMENT_MUTATION = gql(`
  mutation AddSubmissionInternalCommentForStaffLanding(
    $submissionId: ID!
    $comment: String!
  ) {
    addSubmissionInternalComment(submissionId: $submissionId, comment: $comment) {
      id
    }
  }
`);

const DELETE_SUBMISSION_INTERNAL_COMMENT_MUTATION = gql(`
  mutation DeleteSubmissionInternalCommentForStaffLanding($id: ID!) {
    deleteSubmissionInternalComment(id: $id)
  }
`);

type WasteSubmissionDetailData = {
  wasteSubmissionDetail: {
    tenantName: string;
    facilityId: string | null;
    facilityName: string | null;
    state: string | null;
    epaIds: string[];
    ownerName: string | null;
    ownerEmail: string | null;
    staffReviewer: {
      id: string;
      email: string | null;
      firstName: string | null;
      lastName: string | null;
      isSigner: boolean;
    } | null;
    submission: {
      id: string;
      tenantId: string;
      title: string | null;
      dueDate: string | null;
      status: string;
      encampStatus: SubmissionEncampStatus | null;
      submissionType: string;
      usesStepper: boolean;
      revisedFromSubmissionId: string | null;
      revisedFromSubmission: { id: string; title: string | null } | null;
      revisionSubmissions: { id: string; title: string | null }[];
      processedManifestExceptionReport: {
        id: string;
        manifestTrackingNumber: string;
      } | null;
    };
    internalComments: CommentThreadFragment[];
  };
};

type WasteSubmissionDetailVariables = {
  submissionId: string;
};

type AddSubmissionInternalCommentVariables = {
  submissionId: string;
  comment: string;
};

type DeleteSubmissionInternalCommentVariables = {
  id: string;
};

function DetailField({ label, value }: { label: string; value: string }) {
  return (
    <Stack direction="row" spacing={2} alignItems="baseline">
      <Typography
        variant="caption"
        sx={{
          minWidth: "140px",
          color: "text.secondary",
          fontWeight: 500,
          letterSpacing: 0.2,
        }}
      >
        {label}
      </Typography>
      <Typography variant="body2">{value || "—"}</Typography>
    </Stack>
  );
}

export function WasteSubmissionLandingPage() {
  const theme = useTheme();
  const backgroundColor = theme.palette.canvas;
  const { submissionId } = useParams();
  const { user } = useCurrentUser();
  const alerts = useAlerts();
  const [linearDialogOpen, setLinearDialogOpen] = useState(false);
  const [boldSignDialogOpen, setBoldSignDialogOpen] = useState(false);

  const backToListHref = useMemo(() => {
    const savedSearch = sessionStorage.getItem(WASTE_FULFILLMENT_SEARCH_KEY);
    return `/staff/fulfillment/waste${savedSearch ?? ""}`;
  }, []);

  useBreadcrumb([{ label: "Fulfillment", to: backToListHref }, { label: "Waste Submission" }]);

  const { data, loading, error, refetch } = useQuery<
    WasteSubmissionDetailData,
    WasteSubmissionDetailVariables
  >(WASTE_SUBMISSION_DETAIL_QUERY, {
    variables: { submissionId: submissionId ?? "" },
    skip: !submissionId,
    fetchPolicy: "network-only",
  });

  const [addComment, { loading: addingComment }] = useMutation<
    unknown,
    AddSubmissionInternalCommentVariables
  >(ADD_SUBMISSION_INTERNAL_COMMENT_MUTATION);

  const [deleteComment, { loading: deletingComment }] = useMutation<
    unknown,
    DeleteSubmissionInternalCommentVariables
  >(DELETE_SUBMISSION_INTERNAL_COMMENT_MUTATION);

  const submission = data?.wasteSubmissionDetail.submission;
  const staffReviewerText = useMemo(() => {
    const reviewer = data?.wasteSubmissionDetail.staffReviewer;
    if (!reviewer) {
      return "—";
    }
    const name =
      reviewer.firstName || reviewer.lastName
        ? `${reviewer.firstName ?? ""} ${reviewer.lastName ?? ""}`.trim()
        : (reviewer.email ?? "—");
    return reviewer.isSigner ? `${name} (Sign)` : name;
  }, [data?.wasteSubmissionDetail.staffReviewer]);

  const customerSubmissionEditPath = useMemo(() => {
    if (!submission?.tenantId || !submission?.id) return null;
    // Stepper (8700-12 data-entry) submissions open in the stepper, not the
    // document-review edit form.
    if (submission.usesStepper) {
      return `/o/${submission.tenantId}/waste/submissions/${submission.id}/stepper`;
    }
    return `/o/${submission.tenantId}/waste/submissions/${submission.id}/edit`;
  }, [submission?.id, submission?.tenantId, submission?.usesStepper]);

  const commentThreadData = useMemo(() => {
    return data?.wasteSubmissionDetail.internalComments ?? [];
  }, [data?.wasteSubmissionDetail.internalComments]);

  const onAddComment = async (message: string) => {
    if (!submissionId || !message.trim()) return;
    try {
      await addComment({
        variables: {
          submissionId,
          comment: message.trim(),
        },
      });
      await refetch();
    } catch (mutationError) {
      console.error(mutationError);
      alerts.error("Failed to add internal comment.");
    }
  };

  const onDeleteComment = async (commentId: string) => {
    try {
      await deleteComment({ variables: { id: commentId } });
      await refetch();
    } catch (mutationError) {
      console.error(mutationError);
      alerts.error("Failed to delete internal comment.");
    }
  };

  if (!submissionId) {
    return (
      <Alert severity="error">
        No submission id provided. Go back to the waste fulfillment list.
      </Alert>
    );
  }

  if (loading) {
    return (
      <Stack
        p={3}
        sx={{
          background: backgroundColor,
          minHeight: "calc(100vh - 180px)",
        }}
      >
        <Paper elevation={0} sx={{ p: 3 }}>
          <Typography variant="body2" color="text.secondary">
            Loading waste submission...
          </Typography>
        </Paper>
      </Stack>
    );
  }

  if (error || !data?.wasteSubmissionDetail || !submission) {
    return <Alert severity="error">Failed to load waste submission detail.</Alert>;
  }

  return (
    <Stack
      p={3}
      spacing={2}
      sx={{
        background: backgroundColor,
        minHeight: "calc(100vh - 180px)",
      }}
    >
      <Paper elevation={0} sx={{ p: theme.spacing(3) }}>
        <Stack spacing={2}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", sm: "center" }}
            spacing={1.5}
          >
            <Stack spacing={0.5}>
              <Typography variant="h6">{submission.title || "Waste Submission"}</Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                <SubmissionStatusChipWithPopover
                  submissionId={submission.id}
                  submissionStatus={submission.status}
                  encampStatus={submission.encampStatus}
                  isStaff
                  onUpdated={() => refetch()}
                />
                <Typography variant="body2" color="text.secondary">
                  {prettyPrintEnumValue(submission.submissionType)}
                </Typography>
              </Stack>
            </Stack>

            <Stack direction="row" spacing={1} alignItems="center">
              <Tooltip title="Create Linear Ticket">
                <IconButton onClick={() => setLinearDialogOpen(true)} size="small">
                  <LinearIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Button component={Link} to={backToListHref} variant="text">
                Back to List
              </Button>
              {data.wasteSubmissionDetail.facilityId && submission.tenantId ? (
                <Button
                  component={Link}
                  to={`/o/${submission.tenantId}/waste/facilities/${data.wasteSubmissionDetail.facilityId}`}
                  target="_blank"
                  variant="outlined"
                  endIcon={<OpenInNewIcon />}
                >
                  Open Facility Details
                </Button>
              ) : null}
              {customerSubmissionEditPath ? (
                <Button
                  component={Link}
                  to={customerSubmissionEditPath}
                  target="_blank"
                  variant="outlined"
                  endIcon={<OpenInNewIcon />}
                >
                  Open Customer Edit
                </Button>
              ) : null}
            </Stack>
          </Stack>
        </Stack>
      </Paper>

      <SubmissionRevisionBanner
        tenantId={submission.tenantId}
        revisedFromSubmission={submission.revisedFromSubmission}
        revisionSubmissions={submission.revisionSubmissions}
        basePath="/staff/fulfillment/waste/submissions"
      />

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "5fr 7fr" },
          gap: 2,
        }}
      >
        <Paper elevation={0} sx={{ p: theme.spacing(3), height: "100%" }}>
          <Stack spacing={2}>
            <Typography variant="h6">Submission Details</Typography>
            <DetailField label="Organization" value={data.wasteSubmissionDetail.tenantName} />
            <DetailField label="Facility" value={data.wasteSubmissionDetail.facilityName || "—"} />
            <DetailField label="State/Territory" value={data.wasteSubmissionDetail.state || "—"} />
            <DetailField
              label="EPA IDs"
              value={
                data.wasteSubmissionDetail.epaIds.length > 0
                  ? data.wasteSubmissionDetail.epaIds.join(", ")
                  : "—"
              }
            />
            {submission.processedManifestExceptionReport && (
              <Stack direction="row" spacing={2} alignItems="baseline">
                <Typography
                  variant="caption"
                  sx={{
                    minWidth: "140px",
                    color: "text.secondary",
                    fontWeight: 500,
                    letterSpacing: 0.2,
                  }}
                >
                  Manifest
                </Typography>
                <Typography
                  variant="body2"
                  component="a"
                  href={`/o/${submission.tenantId}/waste/manifests/${submission.processedManifestExceptionReport.id}`}
                  sx={{
                    color: "primary.main",
                    textDecoration: "none",
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  {submission.processedManifestExceptionReport.manifestTrackingNumber}
                </Typography>
              </Stack>
            )}
            <DetailField
              label="Due Date"
              value={submission.dueDate ? formatDateLikeForDisplay(submission.dueDate) : "—"}
            />
            <DetailField
              label="Encamp Assignee"
              value={
                data.wasteSubmissionDetail.ownerName || data.wasteSubmissionDetail.ownerEmail || "—"
              }
            />
            <DetailField label="Peer Reviewer" value={staffReviewerText} />
            <Button
              variant="outlined"
              size="small"
              onClick={() => setBoldSignDialogOpen(true)}
              sx={{ alignSelf: "flex-start" }}
            >
              BoldSign Details
            </Button>
          </Stack>
        </Paper>

        <Paper elevation={0} sx={{ p: theme.spacing(3), height: "100%" }}>
          <GenericCommentStream
            title="Internal Comments"
            comments={commentThreadData}
            currentUserId={user?.id}
            onAddComment={onAddComment}
            onDeleteComment={onDeleteComment}
            addLoading={addingComment}
            deleteLoading={deletingComment}
            placeholder="Leave an internal comment"
            buttonText="Comment"
            emptyStateMessage="No internal comments yet."
            showDeleteButton
          />
        </Paper>
      </Box>

      <WasteSubmissionLinearIssueDialog
        open={linearDialogOpen}
        onClose={() => setLinearDialogOpen(false)}
        submissionId={submission.id}
        submissionTitle={submission.title}
        submissionType={prettyPrintEnumValue(submission.submissionType)}
        facilityName={data.wasteSubmissionDetail.facilityName}
        state={data.wasteSubmissionDetail.state}
        tenantName={data.wasteSubmissionDetail.tenantName}
        epaIds={data.wasteSubmissionDetail.epaIds}
      />
      <BoldSignDetailsDialog
        open={boldSignDialogOpen}
        onClose={() => setBoldSignDialogOpen(false)}
        submissionId={submission.id}
      />
    </Stack>
  );
}
